import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback } from "react";

import type { Company } from "../types";

export type CompanyImportSchema = {
  name: string;
  description?: string;
  city?: string;
  country?: string;
  website?: string;
  phone_number?: string;
  linkedin_url?: string;
  tax_identifier?: string;
  sector?: string;
  auditors_count?: string | number;
  size?: string | number;
  address?: string;
  zipcode?: string;
  state_abbr?: string;
  revenue?: string;
  context_links?: string;
  ornz?: string;
  ogrn?: string;
  research_email?: string;
  lead_priority?: string;
  lead_score?: string | number;
  decision_maker?: string;
  decision_maker_role?: string;
  decision_maker_profile_url?: string;
  website_status?: string;
  digital_maturity_score?: string | number;
  specializations?: string;
  social_profile_count?: string | number;
  has_vacancy_signal?: string | boolean;
  vacancy_freshness?: string;
  has_cases_clients?: string | boolean;
  personalized_reason?: string;
  research_checked_at?: string;
  research_source_urls?: string;
  revenue_2025_thousand_rub?: string | number;
  sro_membership_status?: string;
  sro_membership_ended_at?: string;
  sro_membership_end_reason?: string;
  sro_membership_checked_at?: string;
};

function trimOrUndef(value: unknown): string | undefined {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed || undefined;
}

function isRussianRegionLabel(value?: string): value is string {
  if (!value) return false;

  const normalized = value.toLocaleLowerCase("ru");
  const normalizedCity = normalized.replace(/^г\.\s*/, "");

  return (
    normalizedCity === "москва" ||
    normalizedCity === "санкт-петербург" ||
    normalizedCity === "севастополь" ||
    normalized.includes("область") ||
    normalized.includes("край") ||
    normalized.includes("республика") ||
    normalized.includes("автономный округ") ||
    normalized.includes("автономная область")
  );
}

function parsePipeSeparated(raw: unknown): string[] | undefined {
  const value = trimOrUndef(raw);
  if (!value) return undefined;
  const parts = value
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? [...new Set(parts)] : undefined;
}

function parseBoolean(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") return raw;
  const value = trimOrUndef(raw)?.toLocaleLowerCase("ru");
  if (!value) return undefined;
  if (["true", "1", "yes", "да"].includes(value)) return true;
  if (["false", "0", "no", "нет"].includes(value)) return false;
  return undefined;
}

function parseInteger(
  raw: unknown,
  { min = 0, max = Number.MAX_SAFE_INTEGER } = {},
): number | undefined {
  const value = Number(raw);
  if (!Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  return rounded >= min && rounded <= max ? rounded : undefined;
}

function parseLeadPriority(raw: unknown): Company["lead_priority"] | undefined {
  const value = trimOrUndef(raw)?.toLocaleLowerCase("en");
  return value === "high" || value === "medium" || value === "low"
    ? value
    : undefined;
}

function parseSroMembershipStatus(
  raw: unknown,
): Company["sro_membership_status"] | undefined {
  const value = trimOrUndef(raw)?.toLocaleLowerCase("en");
  return value === "active" || value === "ceased" ? value : undefined;
}

function parseAuditorsCount(raw: unknown): Company["size"] | undefined {
  return parseInteger(raw, { min: 1, max: 32_767 });
}

function compactRecord<T extends Record<string, unknown>>(record: T) {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

function buildCompanyData(row: CompanyImportSchema) {
  const city = trimOrUndef(row.city);
  const state_abbr =
    trimOrUndef(row.state_abbr) ??
    (isRussianRegionLabel(city) ? city : undefined);

  return compactRecord({
    name: trimOrUndef(row.name) ?? "",
    description: trimOrUndef(row.description),
    city: state_abbr === city ? undefined : city,
    country: trimOrUndef(row.country),
    website: trimOrUndef(row.website),
    phone_number: trimOrUndef(row.phone_number),
    linkedin_url: trimOrUndef(row.linkedin_url),
    tax_identifier: trimOrUndef(row.tax_identifier),
    sector: trimOrUndef(row.sector),
    size: parseAuditorsCount(row.auditors_count ?? row.size),
    address: trimOrUndef(row.address),
    zipcode: trimOrUndef(row.zipcode),
    state_abbr,
    revenue: trimOrUndef(row.revenue),
    context_links: parsePipeSeparated(row.context_links),
    ornz: trimOrUndef(row.ornz),
    ogrn: trimOrUndef(row.ogrn),
    research_email: trimOrUndef(row.research_email),
    lead_priority: parseLeadPriority(row.lead_priority),
    lead_score: parseInteger(row.lead_score, { min: 0, max: 100 }),
    decision_maker: trimOrUndef(row.decision_maker),
    decision_maker_role: trimOrUndef(row.decision_maker_role),
    decision_maker_profile_url: trimOrUndef(row.decision_maker_profile_url),
    website_status: trimOrUndef(row.website_status),
    digital_maturity_score: parseInteger(row.digital_maturity_score, {
      min: 0,
      max: 10,
    }),
    specializations: parsePipeSeparated(row.specializations),
    social_profile_count: parseInteger(row.social_profile_count),
    has_vacancy_signal: parseBoolean(row.has_vacancy_signal),
    vacancy_freshness: trimOrUndef(row.vacancy_freshness),
    has_cases_clients: parseBoolean(row.has_cases_clients),
    personalized_reason: trimOrUndef(row.personalized_reason),
    research_checked_at: trimOrUndef(row.research_checked_at),
    research_source_urls: parsePipeSeparated(row.research_source_urls),
    revenue_2025_thousand_rub: parseInteger(row.revenue_2025_thousand_rub),
    sro_membership_status: parseSroMembershipStatus(row.sro_membership_status),
    sro_membership_ended_at: trimOrUndef(row.sro_membership_ended_at),
    sro_membership_end_reason: trimOrUndef(row.sro_membership_end_reason),
    sro_membership_checked_at: trimOrUndef(row.sro_membership_checked_at),
  });
}

export function useCompanyImport() {
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  const processBatch = useCallback(
    async (batch: CompanyImportSchema[]) => {
      const rows = batch
        .map((row) => ({
          ...row,
          name: trimOrUndef(row.name) ?? "",
          tax_identifier: trimOrUndef(row.tax_identifier),
        }))
        .filter((row) => row.name);

      const uniqueTaxIdentifiers = [
        ...new Set(
          rows
            .map((row) => row.tax_identifier)
            .filter((value): value is string => Boolean(value)),
        ),
      ];
      const fallbackNames = [...new Set(rows.map((row) => row.name))];

      const [existingByTaxIdentifier, existingByFallbackName] =
        await Promise.all([
          uniqueTaxIdentifiers.length
            ? dataProvider.getList<Company>("companies", {
                filter: {
                  "tax_identifier@in": `(${uniqueTaxIdentifiers
                    .map((identifier) => `"${identifier}"`)
                    .join(",")})`,
                },
                pagination: {
                  page: 1,
                  perPage: uniqueTaxIdentifiers.length,
                },
                sort: { field: "id", order: "ASC" },
              })
            : Promise.resolve({ data: [], total: 0 }),
          fallbackNames.length
            ? dataProvider.getList<Company>("companies", {
                filter: {
                  "name@in": `(${fallbackNames
                    .map((name) => `"${name.replaceAll('"', '\\"')}"`)
                    .join(",")})`,
                },
                pagination: { page: 1, perPage: fallbackNames.length },
                sort: { field: "id", order: "ASC" },
              })
            : Promise.resolve({ data: [], total: 0 }),
        ]);

      const existingByTaxIdentifierMap = new Map(
        existingByTaxIdentifier.data
          .map(
            (company) =>
              [trimOrUndef(company.tax_identifier), company] as const,
          )
          .filter((entry): entry is [string, Company] => Boolean(entry[0])),
      );
      const existingByNameMap = new Map(
        existingByFallbackName.data.map(
          (company) => [company.name.trim(), company] as const,
        ),
      );
      const seenInBatch = new Set<string>();

      await Promise.all(
        rows.map(async (row) => {
          const importKey = row.tax_identifier
            ? `tax:${row.tax_identifier}`
            : `name:${row.name}`;
          if (seenInBatch.has(importKey)) return;
          seenInBatch.add(importKey);

          const companyData = buildCompanyData(row);
          const existingCompany = row.tax_identifier
            ? (existingByTaxIdentifierMap.get(row.tax_identifier) ??
              existingByNameMap.get(row.name))
            : existingByNameMap.get(row.name);

          if (existingCompany) {
            await dataProvider.update("companies", {
              id: existingCompany.id,
              data: companyData,
              previousData: existingCompany,
            });
            return;
          }

          await dataProvider.create("companies", {
            data: {
              ...companyData,
              marketing_consent_status: "unknown",
              do_not_contact: false,
              sales_id: user?.identity?.id,
            },
          });
        }),
      );
    },
    [dataProvider, user?.identity?.id],
  );

  return processBatch;
}
